import {FC, ReactNode, useRef, useState, useEffect} from "react";
import * as Popover from "@radix-ui/react-popover";
import styles from "./styles/EditableFieldStyles.module.scss";
import FontAwesome from "./FontAwesome";

interface EditableFieldProps {
    defaultValue: string;
    isEditing: boolean;
    onEditStart: () => void;
    onSave: (value: string) => void;
    onCancel: () => void;
    placeholder?: string;
    canEdit?: boolean;
    className?: string;
    children: ReactNode;
    multiline?: boolean;
    fullWidth?: boolean;
}

const EditableField: FC<EditableFieldProps> = ({
    defaultValue,
    isEditing,
    onEditStart,
    onSave,
    onCancel,
    placeholder,
    canEdit,
    className,
    children,
    multiline = false,
    fullWidth = false,
}) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [internalValue, setInternalValue] = useState(defaultValue);

    useEffect(() => {
        if (isEditing) {
            setInternalValue(defaultValue);
        }
    }, [isEditing, defaultValue]);

    function handleFieldClick(e: React.MouseEvent<HTMLDivElement>) {
        if (canEdit) {
            onEditStart();
        }
    }

    function handleFieldSave() {
        onSave(internalValue);
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
        if (e.key === 'Enter' && !multiline) {
            handleFieldSave();
        } else if (e.key === 'Enter' && multiline && (e.metaKey || e.ctrlKey)) {
            handleFieldSave();
        } else if (e.key === 'Escape') {
            handleFieldCancel();
        }
    }

    function handleFieldCancel() {
        onCancel();
    }

    if (isEditing) {
        const editingClassName = fullWidth
            ? `${styles.editing} ${styles.editingFullWidth}`
            : styles.editing;

        return (
            <Popover.Root open={true}>
                <div className={editingClassName}>
                    <Popover.Anchor asChild>
                        {multiline ? (
                            <textarea
                                ref={textareaRef}
                                placeholder={placeholder}
                                onChange={(e) => setInternalValue(e.target.value)}
                                value={internalValue}
                                autoFocus
                                onKeyDown={handleKeyDown}
                                aria-label={placeholder || "Edit field"}
                                className={className}
                                rows={4}
                            />
                        ) : (
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder={placeholder}
                                onChange={(e) => setInternalValue(e.target.value)}
                                value={internalValue}
                                autoFocus
                                onKeyDown={handleKeyDown}
                                aria-label={placeholder || "Edit field"}
                                className={className}
                            />
                        )}
                    </Popover.Anchor>
                    <Popover.Portal>
                        <Popover.Content
                            className={styles.buttonContainer}
                            side="bottom"
                            align="end"
                            sideOffset={4}
                            alignOffset={0}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <button
                                type="button"
                                className={styles.saveButton}
                                onClick={handleFieldSave}
                                aria-label="Save changes"
                                title={multiline ? "Save (Cmd/Ctrl+Enter)" : "Save (Enter)"}
                            >
                                <FontAwesome prefix={"fas"} name={"fa-check"} />
                            </button>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleFieldCancel}
                                aria-label="Cancel editing"
                                title="Cancel (Esc)"
                            >
                                <FontAwesome prefix={"fas"} name={"fa-times"} />
                            </button>
                        </Popover.Content>
                    </Popover.Portal>
                </div>
            </Popover.Root>
        )
    }
    else {
        const triggerClassName = canEdit
            ? `${className || ''} ${styles.editableTrigger}`.trim()
            : className;

        return (
            <div
                ref={triggerRef}
                onClick={handleFieldClick}
                className={triggerClassName}
                tabIndex={canEdit ? 0 : -1}
                onKeyDown={(e) => {
                    if (canEdit && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        handleFieldClick(e as any);
                    }
                }}
                role={canEdit ? "button" : undefined}
                aria-label={canEdit ? "Click to edit" : undefined}
            >
                {children}
            </div>
        )
    }
}

export default EditableField;
